import { useState, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import * as yaml from 'js-yaml';
import { ToolLayout } from '../ToolLayout';
import { CodeEditor } from '../CodeEditor';

const SAMPLES: Record<Direction, string> = {
  'json-to-yaml': '{\n  "service": "backstage",\n  "version": "1.3.0",\n  "replicas": 2,\n  "config": {\n    "port": 7007,\n    "logLevel": "info"\n  },\n  "tags": ["platform", "internal"]\n}',
  'yaml-to-json': 'service: backstage\nversion: 1.3.0\nreplicas: 2\nconfig:\n  port: 7007\n  logLevel: info\ntags:\n  - platform\n  - internal\n',
};

type Direction = 'json-to-yaml' | 'yaml-to-json';

const useStyles = makeStyles()((theme) => ({
  layout: { display: 'flex', flexDirection: 'column', gap: theme.spacing(2), height: 'calc(100vh - 260px)', minHeight: 400 },
  controls: { display: 'flex', alignItems: 'center', gap: theme.spacing(2), flexWrap: 'wrap' },
  row: { flex: 1, display: 'flex', gap: theme.spacing(2), alignItems: 'stretch', overflow: 'hidden' },
  pane: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  paneHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    minHeight: 36,
  },
  paneLabel: {
    fontWeight: 600,
    fontSize: 13,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
}));

function convertJsonToYaml(input: string): { result: string; error: null } | { result: null; error: string } {
  try {
    const obj = JSON.parse(input);
    return { result: yaml.dump(obj, { indent: 2 }), error: null };
  } catch (e: any) {
    return { result: null, error: `Invalid JSON: ${e?.message}` };
  }
}

function convertYamlToJson(input: string): { result: string; error: null } | { result: null; error: string } {
  try {
    const obj = yaml.load(input);
    return { result: JSON.stringify(obj, null, 2), error: null };
  } catch (e: any) {
    return { result: null, error: `Invalid YAML: ${e?.message}` };
  }
}

export function JsonYamlPage() {
  const { classes } = useStyles();
  const [direction, setDirection] = useState<Direction>('json-to-yaml');
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const convert = useCallback((value: string, dir: Direction) => {
    if (!value.trim()) { setOutput(''); setError(null); return; }
    const { result, error: err } = dir === 'json-to-yaml' ? convertJsonToYaml(value) : convertYamlToJson(value);
    if (err) { setError(err); setOutput(''); }
    else { setOutput(result!); setError(null); }
  }, []);

  const handleInputChange = (value: string) => {
    setInput(value);
    convert(value, direction);
  };

  const handleDirectionChange = (_: unknown, val: Direction | null) => {
    if (!val) return;
    const newInput = output || input;
    setDirection(val);
    setInput(newInput);
    convert(newInput, val);
  };

  const clear = () => { setInput(''); setOutput(''); setError(null); setCopied(false); };

  const loadSample = () => {
    const sample = SAMPLES[direction];
    setInput(sample);
    convert(sample, direction);
  };

  const copyOutput = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const download = () => {
    const ext = direction === 'json-to-yaml' ? 'yaml' : 'json';
    const mime = direction === 'json-to-yaml' ? 'text/yaml' : 'application/json';
    const blob = new Blob([output], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `output.${ext}`; a.click();
    URL.revokeObjectURL(url);
  };

  const inputLabel = direction === 'json-to-yaml' ? 'JSON Input' : 'YAML Input';
  const outputLabel = direction === 'json-to-yaml' ? 'YAML Output' : 'JSON Output';
  const inputPlaceholder = direction === 'json-to-yaml'
    ? '{\n  "name": "backstage",\n  "version": "1.0.0"\n}'
    : 'name: backstage\nversion: 1.0.0\n';

  return (
    <ToolLayout
      title="JSON ↔ YAML"
      subtitle="Convert between JSON and YAML formats"
    >
      <Box className={classes.layout}>
        <Box className={classes.controls}>
          <ToggleButtonGroup
            size="small"
            value={direction}
            exclusive
            onChange={handleDirectionChange}
          >
            <ToggleButton value="json-to-yaml" sx={{ textTransform: 'none', fontSize: 13 }}>JSON → YAML</ToggleButton>
            <ToggleButton value="yaml-to-json" sx={{ textTransform: 'none', fontSize: 13 }}>YAML → JSON</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Box className={classes.row}>
          <Box className={classes.pane}>
            <Box className={classes.paneHeader}>
              <Typography className={classes.paneLabel}>{inputLabel}</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Button size="small" variant="outlined" onClick={loadSample} sx={{ textTransform: 'none', fontSize: 12 }}>
                  Load sample
                </Button>
                <Tooltip title="Clear">
                  <span>
                    <IconButton size="small" onClick={clear} disabled={!input && !output}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <CodeEditor
              value={input}
              onChange={handleInputChange}
              sx={{ flex: 1 }}
              placeholder={inputPlaceholder}
            />
          </Box>

          <Box className={classes.pane}>
            <Box className={classes.paneHeader}>
              <Typography className={classes.paneLabel}>{outputLabel}</Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title={copied ? 'Copied!' : 'Copy output'}>
                  <span>
                    <IconButton size="small" onClick={copyOutput} disabled={!output}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Download">
                  <span>
                    <IconButton size="small" onClick={download} disabled={!output}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <CodeEditor
              value={output}
              readOnly
              sx={{ flex: 1 }}
              placeholder="Output will appear here…"
            />
          </Box>
        </Box>
      </Box>
    </ToolLayout>
  );
}
